import { t } from "../../i18n";
import { Bell, BellOff, Volume2 } from "lucide-react";
import { useState } from "react";
import { loadOrderSoundEnabled, saveOrderSoundEnabled } from "../../storage";
import { playOrderSound } from "../../useEasyOrders";

type Props = {
  back: React.ReactNode;
  onRequestPermission: () => Promise<NotificationPermission>;
};

type Support = NotificationPermission | "unsupported";

function currentSupport(): Support {
  return "Notification" in window ? Notification.permission : "unsupported";
}

export function NotificationsPage({ back, onRequestPermission }: Props) {
  const [permission, setPermission] = useState<Support>(currentSupport);
  const [sound, setSound] = useState(loadOrderSoundEnabled);
  const [busy, setBusy] = useState(false);

  const request = async (): Promise<void> => {
    setBusy(true);
    try {
      setPermission(await onRequestPermission());
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="screen settings-screen" aria-labelledby="settings-heading">
      {back}
      <div className="screen-heading-row">
        <div>
          <h1 id="settings-heading">{t("Ειδοποιήσεις")}</h1>
          <p>{t("Ήχος και ειδοποιήσεις για αλλαγές παραγγελιών")}</p>
        </div>
      </div>

      <div className="settings-groups">
        <section className="settings-group">
          <h2>{t("Ειδοποιήσεις συσκευής")}</h2>
          {permission === "granted" ? (
            <div className="status-row">
              <Bell />
              <span>
                <strong>{t("Ενεργές")}</strong>
                <small>{t("Θα ειδοποιείσαι όταν η εφαρμογή δεν είναι μπροστά.")}</small>
              </span>
            </div>
          ) : null}
          {permission === "default" ? (
            <>
              <div className="status-row">
                <BellOff />
                <span>
                  <strong>{t("Ανενεργές")}</strong>
                  <small>{t("Χρειάζεται η άδειά σου για να εμφανίζονται ειδοποιήσεις.")}</small>
                </span>
              </div>
              <button
                type="button"
                className="primary-command wide"
                onClick={() => void request()}
                disabled={busy}
              >
                <Bell />
                <span>{t("Ενεργοποίηση ειδοποιήσεων")}</span>
              </button>
            </>
          ) : null}
          {permission === "denied" ? (
            <div className="status-row">
              <BellOff />
              <span>
                <strong>{t("Αποκλεισμένες")}</strong>
                {/* Once denied, the browser will not show the prompt again: only the site
                    settings can undo it, so pointing at the button is the only useful help. */}
                <small>{t("Οι ειδοποιήσεις έχουν αποκλειστεί από τον browser. Άνοιξε τις ρυθμίσεις της σελίδας (το εικονίδιο δίπλα στη διεύθυνση) και επίτρεψέ τις ξανά.")}</small>
              </span>
            </div>
          ) : null}
          {permission === "unsupported" ? (
            <div className="status-row">
              <BellOff />
              <span>
                <strong>{t("Μη διαθέσιμες")}</strong>
                <small>{t("Αυτή η συσκευή δεν υποστηρίζει ειδοποιήσεις web.")}</small>
              </span>
            </div>
          ) : null}
        </section>

        <section className="settings-group">
          <h2>{t("Ήχος")}</h2>
          <label className="check-option">
            <input
              type="checkbox"
              checked={sound}
              onChange={(event) => {
                setSound(event.target.checked);
                saveOrderSoundEnabled(event.target.checked);
              }}
            />
            <span>{t("Ήχος σε νέες παραγγελίες και αλλαγές")}</span>
          </label>
          <p className="settings-note">{t("Ο ήχος παίζει και με την εφαρμογή ανοιχτή, χωρίς να χρειάζεται άδεια.")}</p>
          <button
            type="button"
            className="secondary-command wide"
            onClick={() => playOrderSound()}
          >
            <Volume2 />
            <span>{t("Δοκιμή ήχου")}</span>
          </button>
        </section>
      </div>
    </section>
  );
}
